import { IdentityContext } from '@paperstac/common/lib/components/IdentityProvider';
import useFirestoreSubscribe from '@paperstac/hooks/lib/useFirestoreSubscribe';
import { LISTING_NEGOTIATION_TYPE } from '@paperstac/firestore-collections/lib/listings';
import { settingsRef } from '@paperstac/firestore-collections/lib/settings';
import { TRANSACTION_EVENT_TYPE } from '@paperstac/firestore-collections/lib/transactionEvents';
import getPath from '@paperstac/routes/lib/getPath';
import { MY_FEED_ITEM } from '@paperstac/routes/lib/webRoutes';
import Box from '@paperstac/ui/lib/Box';
import Button from '@paperstac/ui/lib/Button';
import Drawer from '@paperstac/ui/lib/Drawer';
import DrawerHeader from '@paperstac/ui/lib/DrawerHeader';
import ErrorText from '@paperstac/ui/lib/ErrorText';
import Heading from '@paperstac/ui/lib/Heading';
import Link from '@paperstac/ui/lib/Link';
import Overlay from '@paperstac/ui/lib/Overlay';
import Text from '@paperstac/ui/lib/Text';
import get from 'lodash/get';
import React from 'react';
import { object, string } from 'yup';
import NegotiateNotePriceMultiView from '../../common/MakeOfferOverlay/NegotiateNotePriceMultiView';
import NegotiateNotePriceSingleView from '../../common/MakeOfferOverlay/NegotiateNotePriceSingleView';
import VerifyIdentityOverlay from '../../common/VerifyIdentityOverlay/VerifyIdentityOverlay';
import { ListingPageContext } from './ListingPageProvider';
import NegotiateNotePricingTable from './NegotiateNotePricingTable';

export const validator = () =>
  object().shape({
    message: string().required(),
  });

const MakeOfferOverlay = React.memo(() => {
  const { identity } = React.useContext(IdentityContext);
  const { createTransaction, hideOffer, listing, transactionId } = React.useContext(ListingPageContext);
  const [settings] = useFirestoreSubscribe(settingsRef());
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [updatedNotePrices, setUpdatedNotePrices] = React.useState(listing.notePrices);
  const [noteIdToEdit, setNoteIdToEdit] = React.useState(null);
  const [submitError, setSubmitError] = React.useState(null);
  const sellerName = get(listing, 'seller.displayName', 'Paperstac Seller');
  const feedItemUrl = React.useMemo(() => (transactionId ? getPath(MY_FEED_ITEM, transactionId) : ''), [transactionId]);
  const isFirmListing = React.useMemo(
    () => listing.negotiationType === LISTING_NEGOTIATION_TYPE.FIRM,
    [listing.negotiationType]
  );
  const isPool = React.useMemo(() => listing.notes.length > 1, [listing.notes]);
  const noteToEdit = React.useMemo(
    () => listing.notes.find(({ id }) => id === noteIdToEdit),
    [listing.notes, noteIdToEdit]
  );
  const noOp = React.useCallback(() => {}, []);
  const drawerTitle = React.useMemo(() => (isFirmListing ? 'Accept Firm Price' : 'Make an Offer'), [isFirmListing]);

  const beginEdit = React.useCallback((noteId) => setNoteIdToEdit(noteId), []);
  const stopEdit = React.useCallback(() => setNoteIdToEdit(null), []);

  const handleNotePriceChange = React.useCallback(
    (change) => {
      setUpdatedNotePrices({ ...updatedNotePrices, ...change });
      stopEdit();
      return Promise.resolve();
    },
    [stopEdit, updatedNotePrices]
  );

  const handleSubmit = React.useCallback(
    (notePrices) =>
      createTransaction({
        notePrices,
        type: TRANSACTION_EVENT_TYPE.MAKE_OFFER,
      }),
    [createTransaction]
  );
  const handleFirmSubmit = React.useCallback(() => {
    if (isSubmitting) return;

    setIsSubmitting(true);
    setSubmitError(null);

    createTransaction({
      notePrices: listing.notePrices,
      type: TRANSACTION_EVENT_TYPE.ACCEPT_PRICE,
    })
      .catch((error) => setSubmitError(error.message))
      .finally(() => setIsSubmitting(false));
  }, [createTransaction, isSubmitting, listing.notePrices]);

  if (settings?.requireVerificationToCreateTransaction && !identity.isVerified)
    return <VerifyIdentityOverlay onClose={hideOffer} />;

  return (
    <>
      <Overlay onClose={hideOffer}>
        <Drawer width={600}>
          <DrawerHeader onClose={hideOffer} text={drawerTitle} />
          <Box sx={{ p: 4, overflowY: 'auto', flex: '1 1 auto' }}>
            {!feedItemUrl && isFirmListing && (
              <Box>
                <Heading mb={3}>Accept Firm Price</Heading>
                <Text mb={4}>
                  This listing is marked as firm. Accepting will create a transaction at the seller&apos;s current
                  pricing.
                </Text>
                <NegotiateNotePricingTable
                  editable={false}
                  mb={4}
                  notePrices={listing.notePrices}
                  notes={listing.notes}
                  onEdit={noOp}
                  updatedNotePrices={listing.notePrices}
                />
                {!!submitError && <ErrorText mb={3}>{submitError}</ErrorText>}
                <Button busy={isSubmitting} mr={2} onClick={handleFirmSubmit} variant="primary">
                  Accept Firm Price
                </Button>
                <Button onClick={hideOffer} variant="default">
                  Cancel
                </Button>
              </Box>
            )}

            {!feedItemUrl && !isFirmListing && isPool && !noteToEdit && (
              <NegotiateNotePriceMultiView
                notePrices={listing.notePrices}
                notes={listing.notes}
                onCancel={hideOffer}
                onEdit={beginEdit}
                onSubmit={handleSubmit}
                updatedNotePrices={updatedNotePrices}
              />
            )}

            {!feedItemUrl && !isFirmListing && isPool && !!noteToEdit && (
              <NegotiateNotePriceSingleView
                differentPriceActionText="Adjust Price"
                notePrice={listing.notePrices[noteIdToEdit] || ''}
                note={noteToEdit}
                onCancel={stopEdit}
                onSubmit={handleNotePriceChange}
                samePriceActionText="Keep Current Price"
                updatedNotePrice={updatedNotePrices[noteIdToEdit] || ''}
              />
            )}

            {!feedItemUrl && !isFirmListing && !isPool && (
              <NegotiateNotePriceSingleView
                notePrice={listing.notePrices[listing.notes[0].id] || ''}
                note={listing.notes[0]}
                onCancel={hideOffer}
                onSubmit={handleSubmit}
                updatedNotePrice={listing.notePrices[listing.notes[0].id] || ''}
              />
            )}

            {!!feedItemUrl && (
              <Box>
                <Heading mb={4}>{isFirmListing ? 'Firm Price Accepted' : 'Offer Made Successfully'}</Heading>
                <Text mb={4} fontSize={1}>
                  {isFirmListing ? (
                    <>
                      Paperstac has successfully created a <Link to={feedItemUrl}>transaction</Link> between you and{' '}
                      <Text variant="bold">{sellerName}</Text> at the seller&apos;s firm price. In that transaction, you
                      can discuss this listing and complete a purchase with our online note closings.
                    </>
                  ) : (
                    <>
                      Paperstac has successfully delivered your offer. We have also created a{' '}
                      <Link to={feedItemUrl}>transaction</Link> between you and <Text variant="bold">{sellerName}</Text>
                      . In that transaction, you can discuss this listing, further negotiate pricing and even make a
                      purchase with our online note closings.
                    </>
                  )}
                </Text>
                <Button as={Link} mr={2} to={feedItemUrl} variant="primary">
                  View Transaction
                </Button>
                <Button onClick={hideOffer} variant="default">
                  Close
                </Button>
              </Box>
            )}
          </Box>
        </Drawer>
      </Overlay>
    </>
  );
});

MakeOfferOverlay.propTypes = {};

MakeOfferOverlay.defaultProps = {};

MakeOfferOverlay.displayName = 'MakeOfferOverlay';

export default MakeOfferOverlay;
